import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { planningCyclesService } from '@/services/demandPlanning/planningCycles.service';

import type { EntityScopeFilters } from '../components/ConsensusScopeFilters';
import { entityScopeFiltersKey } from '../components/ConsensusScopeFilters';
import type {
  ConsensusChartHistoryRange,
  ConsensusOverviewPayload,
} from '../types/consensus.types';
import type { ChartGranularity } from '../utils/chartLabels';
import { appendEntityScopeToSearchParams } from '../utils/entityScopeParams';

const CHART_STALE_MS = 30_000;
const META_STALE_MS = 5 * 60_000;
const SCENARIOS_STALE_MS = 5 * 60_000;

function buildParams(
  cycleId: number,
  include: string,
  historyRange: ConsensusChartHistoryRange,
  granularity: ChartGranularity,
  entityFilters: EntityScopeFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('planning_cycle', String(cycleId));
  params.set('include', include);
  params.set('history_weeks', '52');
  params.set('history_range', historyRange);
  params.set('granularity', granularity);
  appendEntityScopeToSearchParams(params, entityFilters);
  return params;
}

/** Hipótesis + familias del plan; solo depende del ciclo (compartido entre tabs). */
export function useConsensusCycleMeta(selectedCycleId: number | null) {
  return useQuery({
    queryKey: ['consensus-overview', 'meta', selectedCycleId],
    queryFn: () =>
      planningCyclesService.getOverview(selectedCycleId!, {
        include: 'assumptions,plan_scope',
      }),
    enabled: selectedCycleId != null,
    staleTime: META_STALE_MS,
  });
}

export function useConsensusOverviewQueries(
  selectedCycleId: number | null,
  historyRange: ConsensusChartHistoryRange,
  granularity: ChartGranularity,
  entityFilters: EntityScopeFilters,
  options?: { loadScenarios?: boolean },
) {
  const loadScenarios = options?.loadScenarios ?? true;
  const scopeKey = entityScopeFiltersKey(entityFilters);

  const metaQuery = useConsensusCycleMeta(selectedCycleId);

  const chartQuery = useQuery({
    queryKey: [
      'consensus-overview',
      'chart',
      selectedCycleId,
      historyRange,
      granularity,
      scopeKey,
      entityFilters,
    ],
    queryFn: () => {
      const params = buildParams(selectedCycleId!, 'chart', historyRange, granularity, entityFilters);
      const planVersionId = metaQuery.data?.plan_version?.id;
      if (planVersionId != null) {
        params.set('plan_version', String(planVersionId));
      }
      return planningCyclesService.getOverviewFromParams(params);
    },
    enabled: selectedCycleId != null,
    staleTime: CHART_STALE_MS,
  });

  const scenariosQuery = useQuery({
    queryKey: ['consensus-overview', 'scenarios', selectedCycleId, scopeKey, entityFilters],
    queryFn: () =>
      planningCyclesService.getOverviewFromParams(
        buildParams(selectedCycleId!, 'scenarios', historyRange, granularity, entityFilters),
      ),
    enabled: selectedCycleId != null && loadScenarios,
    staleTime: SCENARIOS_STALE_MS,
  });

  const data = useMemo((): ConsensusOverviewPayload | undefined => {
    if (!chartQuery.data && !metaQuery.data) return undefined;
    return {
      ...(metaQuery.data ?? {}),
      ...(chartQuery.data ?? {}),
      assumptions: metaQuery.data?.assumptions ?? chartQuery.data?.assumptions ?? [],
      plan_scope: metaQuery.data?.plan_scope ?? chartQuery.data?.plan_scope,
      scenario_comparison:
        scenariosQuery.data?.scenario_comparison ?? chartQuery.data?.scenario_comparison,
    } as ConsensusOverviewPayload;
  }, [metaQuery.data, chartQuery.data, scenariosQuery.data]);

  const isLoading = chartQuery.isLoading || metaQuery.isLoading;
  const isError = chartQuery.isError || metaQuery.isError || scenariosQuery.isError;
  const error = chartQuery.error ?? metaQuery.error ?? scenariosQuery.error;

  return {
    data,
    metaQuery,
    chartQuery,
    scenariosQuery,
    isLoading,
    isLoadingScenarios: scenariosQuery.isLoading,
    isError,
    error,
    refetchChart: chartQuery.refetch,
    refetchScenarios: scenariosQuery.refetch,
  };
}

export const consensusOverviewQueryKeys = {
  metaPrefix: ['consensus-overview', 'meta'] as const,
  chartPrefix: ['consensus-overview', 'chart'] as const,
  scenariosPrefix: ['consensus-overview', 'scenarios'] as const,
  allPrefix: ['consensus-overview'] as const,
};
